import { clamp } from "@/utils/math"
import { hsvToRGB } from "@/core/utils/color-conversion"
import { vec3 } from "gl-matrix"
import { logColorMatch, normalizeToFloat32Array } from "./utils"
import { fitToSize, resizeImageBitmapToBitmap } from "@/core/utils/resize-image"
import { bitmapToImageData, imageDataToBitmap, PixelData } from "@/core/utils/image-data"
import { rgbToHue } from "./color-space"
import { ColorSpaceCpu, renderCpu, RenderOptions, setHue } from "./renderer-cpu"
import { debugSaveBitmap } from "../utils"
import { Adjustments } from "@/core/types"

/**
 * Apply 1D LUT to an image
 * @param image Float32Array of shape (H, W, 3) with values in [0, 1]
 * @param lut1d Float32Array of shape (3, M) where M is LUT size
 * @returns transformed image of shape (H, W, 3)
 */
// export function apply1DLUT(image: Float32Array, lut1d: Float32Array): Float32Array {
// 	const M = lut1d.length / 3
// 	const result = new Float32Array(image.length)

// 	for (let idx = 0; idx < image.length; idx += 3) {
// 		for (let c = 0; c < 3; c++) {
// 			// Scale input value to LUT index
// 			const scaled = (image[idx + c] as number) * (M - 1)

// 			// Get lower and upper indices
// 			const idxLow = Math.min(Math.floor(scaled), M - 2)
// 			const idxHigh = idxLow + 1

// 			// Get interpolation weight
// 			const frac = scaled - idxLow

// 			// Linear interpolation
// 			const lutLow = lut1d[c * M + idxLow] as number
// 			const lutHigh = lut1d[c * M + idxHigh] as number
// 			result[idx + c] = clamp(lutLow * (1 - frac) + lutHigh * frac, 0, 1)
// 		}
// 	}

// 	return result
// }
export function apply1DLUT(image: Float32Array, lut1d: Float32Array): Float32Array {
	const M = lut1d.length / 3
	const result = new Float32Array(image.length)

	// --- OPTIMIZATION START ---
	// Hoist constant calculations out of the loop.
	const scaleFactor = M - 1
	const mMinus2 = M - 2 // For clamping the index.

	// Pre-calculate base indices for each color channel's slice of the LUT.
	const rBase = 0
	const gBase = M
	const bBase = M * 2

	for (let idx = 0; idx < image.length; idx += 3) {
		// --- Red Channel ---
		const r_scaled = (image[idx] as number) * scaleFactor
		const r_idxLow = Math.min(Math.floor(r_scaled), mMinus2)
		const r_frac = r_scaled - r_idxLow
		const r_lutLow = lut1d[rBase + r_idxLow] as number
		const r_lutHigh = lut1d[rBase + r_idxLow + 1] as number
		result[idx] = clamp(r_lutLow * (1 - r_frac) + r_lutHigh * r_frac, 0, 1)

		// --- Green Channel ---
		const g_scaled = (image[idx + 1] as number) * scaleFactor
		const g_idxLow = Math.min(Math.floor(g_scaled), mMinus2)
		const g_frac = g_scaled - g_idxLow
		const g_lutLow = lut1d[gBase + g_idxLow] as number
		const g_lutHigh = lut1d[gBase + g_idxLow + 1] as number
		result[idx + 1] = clamp(g_lutLow * (1 - g_frac) + g_lutHigh * g_frac, 0, 1)

		// --- Blue Channel ---
		const b_scaled = (image[idx + 2] as number) * scaleFactor
		const b_idxLow = Math.min(Math.floor(b_scaled), mMinus2)
		const b_frac = b_scaled - b_idxLow
		const b_lutLow = lut1d[bBase + b_idxLow] as number
		const b_lutHigh = lut1d[bBase + b_idxLow + 1] as number
		result[idx + 2] = clamp(b_lutLow * (1 - b_frac) + b_lutHigh * b_frac, 0, 1)
	}
	// --- OPTIMIZATION END ---

	return result
}

/**
 * Apply 3D LUT to an image
 * @param image Float32Array of shape (H, W, 3) with values in [0, 1]
 * @param lut3d Float32Array of shape (3, M, M, M) where M is LUT size
 * @returns transformed image of shape (H, W, 3)
 */
// export function apply3DLUT(
// 	image: Float32Array,
// 	lut3d: Float32Array,
// 	lutSize: number,
// ): Float32Array {
// 	const result = new Float32Array(image.length)
// 	const M = lutSize

// 	for (let idx = 0; idx < image.length; idx += 3) {
// 		// Scale to LUT indices (BGR order)
// 		const scaled = [
// 			(image[idx + 2] as number) * (M - 1), // B
// 			(image[idx + 1] as number) * (M - 1), // G
// 			(image[idx] as number) * (M - 1), // R
// 		]

// 		// Get lower and upper indices
// 		const idxLow = scaled.map((s) => Math.min(Math.floor(s), M - 2))
// 		const idxHigh = idxLow.map((i) => i + 1)

// 		// Get interpolation weights
// 		const frac = scaled.map((s, i) => s - (idxLow[i] as number))

// 		// Get the eight neighboring points
// 		const [x0, y0, z0] = idxLow as [number, number, number]
// 		const [x1, y1, z1] = idxHigh as [number, number, number]
// 		const [fx, fy, fz] = frac as [number, number, number]

// 		// Helper function to get LUT value
// 		const getLUTValue = (x: number, y: number, z: number, c: number): number =>
// 			lut3d[c * M * M * M + x * M * M + y * M + z] as number

// 		// Trilinear interpolation for each channel
// 		for (let c = 0; c < 3; c++) {
// 			const c000 = getLUTValue(x0, y0, z0, c)
// 			const c001 = getLUTValue(x0, y0, z1, c)
// 			const c010 = getLUTValue(x0, y1, z0, c)
// 			const c011 = getLUTValue(x0, y1, z1, c)
// 			const c100 = getLUTValue(x1, y0, z0, c)
// 			const c101 = getLUTValue(x1, y0, z1, c)
// 			const c110 = getLUTValue(x1, y1, z0, c)
// 			const c111 = getLUTValue(x1, y1, z1, c)

// 			const c00 = c000 * (1 - fx) + c100 * fx
// 			const c01 = c001 * (1 - fx) + c101 * fx
// 			const c10 = c010 * (1 - fx) + c110 * fx
// 			const c11 = c011 * (1 - fx) + c111 * fx

// 			const c0 = c00 * (1 - fy) + c10 * fy
// 			const c1 = c01 * (1 - fy) + c11 * fy

// 			result[idx + c] = clamp(c0 * (1 - fz) + c1 * fz, 0, 1)
// 		}
// 	}

// 	return result
// }
export function apply3DLUT(
	image: Float32Array,
	lut3d: Float32Array,
	lutSize: number,
): Float32Array {
	const result = new Float32Array(image.length)
	const M = lutSize

	// --- OPTIMIZATION START (Corrected) ---
	// Hoist constant calculations out of the loop.
	const scaleFactor = M - 1
	const mMinus2 = M - 2 // For clamping the index.
	const M2 = M * M
	const M3 = M * M * M

	// Pre-calculate base offsets for each color channel's slice of the 3D LUT.
	const rLutBase = 0
	const gLutBase = M3
	const bLutBase = M3 * 2

	for (let idx = 0; idx < image.length; idx += 3) {
		// --- Calculate indices and fractions from pixel values ---
		// The original code used BGR values as coordinates into the LUT's XYZ axes.
		const x_scaled = (image[idx + 2] as number) * scaleFactor // X coord from Blue
		const y_scaled = (image[idx + 1] as number) * scaleFactor // Y coord from Green
		const z_scaled = (image[idx] as number) * scaleFactor // Z coord from Red

		const x0 = Math.min(Math.floor(x_scaled), mMinus2)
		const y0 = Math.min(Math.floor(y_scaled), mMinus2)
		const z0 = Math.min(Math.floor(z_scaled), mMinus2)

		const x1 = x0 + 1
		const y1 = y0 + 1
		const z1 = z0 + 1

		// Interpolation weights (fractions)
		const fx = x_scaled - x0
		const fy = y_scaled - y0
		const fz = z_scaled - z0

		// --- Calculate 1D indices for the 8 corners, matching original formula ---
		const i000 = x0 * M2 + y0 * M + z0
		const i001 = x0 * M2 + y0 * M + z1
		const i010 = x0 * M2 + y1 * M + z0
		const i011 = x0 * M2 + y1 * M + z1
		const i100 = x1 * M2 + y0 * M + z0
		const i101 = x1 * M2 + y0 * M + z1
		const i110 = x1 * M2 + y1 * M + z0
		const i111 = x1 * M2 + y1 * M + z1

		// --- Trilinear interpolation for RED channel ---
		const r_c000 = lut3d[rLutBase + i000] as number
		const r_c100 = lut3d[rLutBase + i100] as number
		const r_c010 = lut3d[rLutBase + i010] as number
		const r_c110 = lut3d[rLutBase + i110] as number
		const r_c001 = lut3d[rLutBase + i001] as number
		const r_c101 = lut3d[rLutBase + i101] as number
		const r_c011 = lut3d[rLutBase + i011] as number
		const r_c111 = lut3d[rLutBase + i111] as number

		const r_c00 = r_c000 * (1 - fx) + r_c100 * fx
		const r_c10 = r_c010 * (1 - fx) + r_c110 * fx
		const r_c01 = r_c001 * (1 - fx) + r_c101 * fx
		const r_c11 = r_c011 * (1 - fx) + r_c111 * fx

		const r_c0 = r_c00 * (1 - fy) + r_c10 * fy
		const r_c1 = r_c01 * (1 - fy) + r_c11 * fy
		result[idx] = clamp(r_c0 * (1 - fz) + r_c1 * fz, 0, 1)

		// --- Trilinear interpolation for GREEN channel ---
		const g_c000 = lut3d[gLutBase + i000] as number
		const g_c100 = lut3d[gLutBase + i100] as number
		const g_c010 = lut3d[gLutBase + i010] as number
		const g_c110 = lut3d[gLutBase + i110] as number
		const g_c001 = lut3d[gLutBase + i001] as number
		const g_c101 = lut3d[gLutBase + i101] as number
		const g_c011 = lut3d[gLutBase + i011] as number
		const g_c111 = lut3d[gLutBase + i111] as number

		const g_c00 = g_c000 * (1 - fx) + g_c100 * fx
		const g_c10 = g_c010 * (1 - fx) + g_c110 * fx
		const g_c01 = g_c001 * (1 - fx) + g_c101 * fx
		const g_c11 = g_c011 * (1 - fx) + g_c111 * fx

		const g_c0 = g_c00 * (1 - fy) + g_c10 * fy
		const g_c1 = g_c01 * (1 - fy) + g_c11 * fy
		result[idx + 1] = clamp(g_c0 * (1 - fz) + g_c1 * fz, 0, 1)

		// --- Trilinear interpolation for BLUE channel ---
		const b_c000 = lut3d[bLutBase + i000] as number
		const b_c100 = lut3d[bLutBase + i100] as number
		const b_c010 = lut3d[bLutBase + i010] as number
		const b_c110 = lut3d[bLutBase + i110] as number
		const b_c001 = lut3d[bLutBase + i001] as number
		const b_c101 = lut3d[bLutBase + i101] as number
		const b_c011 = lut3d[bLutBase + i011] as number
		const b_c111 = lut3d[bLutBase + i111] as number

		const b_c00 = b_c000 * (1 - fx) + b_c100 * fx
		const b_c10 = b_c010 * (1 - fx) + b_c110 * fx
		const b_c01 = b_c001 * (1 - fx) + b_c101 * fx
		const b_c11 = b_c011 * (1 - fx) + b_c111 * fx

		const b_c0 = b_c00 * (1 - fy) + b_c10 * fy
		const b_c1 = b_c01 * (1 - fy) + b_c11 * fy
		result[idx + 2] = clamp(b_c0 * (1 - fz) + b_c1 * fz, 0, 1)
	}
	// --- OPTIMIZATION END ---

	return result
}

/**
 * Transform image using 1D LUT followed by 3D LUT
 * @param image Float32Array of shape (H, W, 3) with values in [0, 1]
 * @param lut1d Float32Array of shape (3, M)
 * @param lut3d Float32Array of shape (3, M, M, M)
 * @returns transformed image of shape (H, W, 3)
 */
export function transformImage({
	image,
	lut1d,
	lut3d,
	colorStrength = 1,
	lightStrength = 1,
	originalInput,
}: {
	image: Float32Array
	lut1d: Float32Array
	lut3d: Float32Array
	colorStrength?: number
	lightStrength?: number
	originalInput?: Float32Array
}): Float32Array {
	// Apply 1D LUT
	const intermediate = apply1DLUT(image, lut1d)

	// Apply 3D LUT
	let result = apply3DLUT(intermediate, lut3d, lut1d.length / 3)

	if (colorStrength != 1 || (lightStrength != 1 && originalInput)) {
		result = applyColorLightStrength(image, result, colorStrength, lightStrength, originalInput)
	}

	return result
}

function getLuma(pixel: vec3): number {
	return 0.299 * pixel[0] + 0.587 * pixel[1] + 0.114 * pixel[2]
}

function applyColorLightStrength(
	image: Float32Array,
	result: Float32Array,
	colorStrength: number,
	lightStrength: number,
	originalInput?: Float32Array,
): Float32Array {
	const output = new Float32Array(image.length)
	const invColorStrength = 1 - colorStrength
	const invLightStrength = 1 - lightStrength

	const initialPixel: vec3 = [0, 0, 0]
	const resultPixel: vec3 = [0, 0, 0]
	const mixedPixel: vec3 = [0, 0, 0]
	const originalPixel: vec3 = [0, 0, 0]
	const correctedPixel: vec3 = [0, 0, 0]

	for (let idx = 0; idx < image.length; idx += 3) {
		// Get original and result pixels into temporary vectors
		initialPixel[0] = image[idx] as number
		initialPixel[1] = image[idx + 1] as number
		initialPixel[2] = image[idx + 2] as number

		resultPixel[0] = result[idx] as number
		resultPixel[1] = result[idx + 1] as number
		resultPixel[2] = result[idx + 2] as number

		// Mix initial and result based on colorStrength
		mixedPixel[0] = initialPixel[0] * invColorStrength + resultPixel[0] * colorStrength
		mixedPixel[1] = initialPixel[1] * invColorStrength + resultPixel[1] * colorStrength
		mixedPixel[2] = initialPixel[2] * invColorStrength + resultPixel[2] * colorStrength

		// Calculate target luminance
		const resultLuma = getLuma(resultPixel) // this is the target luma if light strength is 1
		let targetLuma = resultLuma
		if (originalInput && lightStrength !== 1) {
			originalPixel[0] = originalInput[idx] as number
			originalPixel[1] = originalInput[idx + 1] as number
			originalPixel[2] = originalInput[idx + 2] as number
			const originalLuma = getLuma(originalPixel)
			targetLuma = resultLuma * lightStrength + originalLuma * invLightStrength
		}
		const mixedLuma = getLuma(mixedPixel)

		// Calculate luminance delta
		const deltaLuma = targetLuma - mixedLuma

		// Preserve original hue
		const resultHue = rgbToHue(mixedPixel)

		// Apply luminance correction to preserve original luma
		correctedPixel[0] = mixedPixel[0] + deltaLuma
		correctedPixel[1] = mixedPixel[1] + deltaLuma
		correctedPixel[2] = mixedPixel[2] + deltaLuma

		// Set the hue to match the result pixel's hue
		// The result of setHue will be written directly into _acs_correctedPixel
		setHue(correctedPixel, correctedPixel, resultHue)

		// Store in output
		output[idx] = clamp(correctedPixel[0], 0, 1)
		output[idx + 1] = clamp(correctedPixel[1], 0, 1)
		output[idx + 2] = clamp(correctedPixel[2], 0, 1)
	}

	return output
}

export function transformImageWithMergedLut({
	image,
	mergedLut3d,
}: {
	image: Float32Array
	mergedLut3d: Float32Array
}): Float32Array {
	// Reshape and transpose the LUT from (25*25*25*3, rgbc) to (3*25*25*25, cBGR)
	const size = 25
	const reshapedLut = new Float32Array(mergedLut3d.length)

	for (let r = 0; r < size; r++) {
		for (let g = 0; g < size; g++) {
			for (let b = 0; b < size; b++) {
				for (let c = 0; c < 3; c++) {
					// Source index in merged LUT (RGBc order)
					const srcIdx = (r * size * size + g * size + b) * 3 + c
					// Destination index in reshaped LUT (cBGR order)
					const dstIdx = c * size * size * size + b * size * size + g * size + r
					;(reshapedLut[dstIdx] as number) = mergedLut3d[srcIdx] as number
				}
			}
		}
	}

	return apply3DLUT(image, reshapedLut, size)
}

export function getMergedLut(lut1d: Float32Array, lut3d: Float32Array): Float32Array {
	const mergedLutSize = 25
	const haldImage = new Float32Array(mergedLutSize * mergedLutSize * mergedLutSize * 3)
	const interval = 1 / (mergedLutSize - 1)
	for (let r = 0; r < mergedLutSize; r++) {
		for (let g = 0; g < mergedLutSize; g++) {
			for (let b = 0; b < mergedLutSize; b++) {
				const index = (r * mergedLutSize + g) * mergedLutSize + b
				haldImage[index * 3] = r * interval
				haldImage[index * 3 + 1] = g * interval
				haldImage[index * 3 + 2] = b * interval
			}
		}
	}
	const lut = transformImage({
		image: haldImage,
		lut1d: lut1d,
		lut3d: lut3d,
	})
	return lut
}

export function createTestPattern(width: number = 256, useSrgbHues: boolean = true): Float32Array {
	const height = 9
	const result = new Float32Array(height * width * 3)

	// Row 0: Grayscale gradient
	for (let x = 0; x < width; x++) {
		const value = x / (width - 1)
		const idx = x * 3
		result[idx] = value
		result[idx + 1] = value
		result[idx + 2] = value
	}

	// Rows 1-9: Major hues
	const hues = [0, 95, 230, 30, 55, 160, 270, 330] // r, g, b, orange, yellow, cyan, purple, magenta
	const srgbHues = [355, 96, 211, 34, 52, 165, 289, 320]
	const activeHues = useSrgbHues ? srgbHues : hues

	const chunk = 16
	for (let row = 1; row < height; row++) {
		const hue = (activeHues[row - 1] as number) / 360

		for (let i = 0; i < chunk; i++) {
			const sat = 0.05 + (i / (chunk - 1)) * 0.95

			for (let j = 0; j < chunk; j++) {
				const value = 0.05 + (j / (chunk - 1)) * 0.95

				// Convert HSV to RGB
				const rgb = hsvToRGB([hue, sat, value])

				// Map the chunk coordinates to image coordinates
				const x = i * chunk + j
				const idx = (row * width + x) * 3

				result[idx] = rgb[0]
				result[idx + 1] = rgb[1]
				result[idx + 2] = rgb[2]
			}
		}
	}

	return result
}

export function getGrayBand(image: Float32Array): Float32Array {
	return image.slice(0, 256 * 3) // first row of the image
}

export function createGrayBand(width: number = 256): Float32Array {
	const result = new Float32Array(width * 3)

	// Grayscale gradient
	for (let x = 0; x < width; x++) {
		const value = x / (width - 1)
		const idx = x * 3
		result[idx] = value
		result[idx + 1] = value
		result[idx + 2] = value
	}

	return result
}

export function extractColors(options: {
	rgbData: Uint8Array // rgb data of the image, no alpha channel
	binSize?: number // size of each color bin (smaller = more precise but more colors), in [0,255]
	pixelCountThreshold?: number
	faceColors?: Float32Array | null
	faceWeights?: Float32Array | null
	maxCount?: number
}): { colors: Float32Array; weights: number[] } {
	const {
		rgbData,
		binSize = 8,
		pixelCountThreshold = 5,
		faceColors,
		faceWeights,
		maxCount = 2500,
	} = options

	const numBins = 256 / binSize
	const numBinsSq = numBins * numBins
	const totalBins = numBins * numBins * numBins
	const binSizeInv = 1.0 / binSize

	// Use flat typed arrays for sums and counts to avoid Map overhead and string keys.
	// Use Uint32 for sums as they will not exceed the integer limit.
	const sums = new Uint32Array(totalBins * 3) // Store R, G, B sums interleaved
	const counts = new Uint32Array(totalBins)

	// A single pass over the pixel data to calculate sums and counts for each bin.
	for (let i = 0; i < rgbData.length; i += 3) {
		const r = rgbData[i] as number
		const g = rgbData[i + 1] as number
		const b = rgbData[i + 2] as number

		// Calculate bin index for this pixel. Clamping is not necessary if input is guaranteed 0-255.
		const r_idx = Math.floor(r * binSizeInv)
		const g_idx = Math.floor(g * binSizeInv)
		const b_idx = Math.floor(b * binSizeInv)

		// Flatten 3D index to a 1D index for direct array access.
		const binIndex = r_idx * numBinsSq + g_idx * numBins + b_idx
		const sumIndex = binIndex * 3

		// Add pixel values to the sum for this bin.
		sums[sumIndex] = (sums[sumIndex] as number) + r
		sums[sumIndex + 1] = (sums[sumIndex + 1] as number) + g
		sums[sumIndex + 2] = (sums[sumIndex + 2] as number) + b
		counts[binIndex] = (counts[binIndex] as number) + 1
	}

	// Calculate average colors for each bin that actually contains pixels.
	const colorCounts: Array<[vec3, number]> = []
	for (let i = 0; i < totalBins; i++) {
		const count = counts[i] ?? 0
		if (count > pixelCountThreshold) {
			const sumIndex = i * 3
			// The average color should be normalized back to the [0, 255] range for consistency.
			const avgColor: vec3 = [
				(sums[sumIndex] as number) / count,
				(sums[sumIndex + 1] as number) / count,
				(sums[sumIndex + 2] as number) / count,
			]
			colorCounts.push([avgColor, count])
		}
	}

	// Sort by frequency descending
	let sortedEntries = colorCounts.sort((a, b) => b[1] - a[1])
	logColorMatch("filtered quantized colors:", sortedEntries.length)

	// Filter and convert to final format
	const totalPixels = rgbData.length / 3

	const selectedCount = Math.min(
		Math.max(sortedEntries.length, 10),
		Math.min(maxCount, sortedEntries.length),
	)

	sortedEntries = sortedEntries
		.slice(0, selectedCount)
		.map(([color, count]) => [
			[
				roundToDigits(color[0] / 255, 5),
				roundToDigits(color[1] / 255, 5),
				roundToDigits(color[2] / 255, 5),
			],
			roundToDigits(count / totalPixels, 5),
		])

	if (faceColors && faceWeights) {
		for (let i = 0; i < Math.min(faceColors.length, 300); i += 3) {
			const color = [faceColors[i], faceColors[i + 1], faceColors[i + 2]] as vec3
			const weight = (faceWeights[i / 3] as number) * 0.04
			sortedEntries.push([color, weight])
		}
		sortedEntries.sort((a, b) => b[1] - a[1])
	}

	// Create output arrays
	const colors = new Float32Array(sortedEntries.length * 3)
	let weights: number[] = []

	sortedEntries.forEach(([color, weight], index) => {
		colors[index * 3] = color[0]
		colors[index * 3 + 1] = color[1]
		colors[index * 3 + 2] = color[2]
		weights.push(weight)
	})

	// normalize weights
	const totalWeight = weights.reduce((a, b) => a + b, 0)
	weights = weights.map((w) => w / totalWeight)

	return { colors, weights }
}

function roundToDigits(value: number, digits: number): number {
	const factor = Math.pow(10, digits)
	return Math.round(value * factor) / factor
}

export function getRgbData(
	image: ImageData | PixelData,
	faceRgbPixels?: Uint8Array | null,
): Uint8Array {
	const totalPixels = image.width * image.height

	const facePixelCount = (faceRgbPixels?.length ?? 0) / 3

	// Convert to RGB and normalize to [0,1]
	const rgbData = new Uint8Array((totalPixels + facePixelCount) * 3)

	for (let i = 0; i < totalPixels; i++) {
		rgbData[i * 3] = image.data[i * 4] ?? 0 // R
		rgbData[i * 3 + 1] = image.data[i * 4 + 1] ?? 0 // G
		rgbData[i * 3 + 2] = image.data[i * 4 + 2] ?? 0 // B
	}

	// Add face pixels as an extra (around 1200 pixels, < 0.1 x totalPixels) to emphasize them
	if (faceRgbPixels) {
		rgbData.set(faceRgbPixels, totalPixels * 3)
	}

	return rgbData
}

export function rgbaToRgbData(rgba: Uint8Array): Uint8Array {
	const totalPixels = rgba.length / 4
	const rgbData = new Uint8Array(totalPixels * 3)

	let rgbIndex = 0
	for (let i = 0; i < rgba.length; i += 4) {
		rgbData[rgbIndex++] = rgba[i] ?? 0
		rgbData[rgbIndex++] = rgba[i + 1] ?? 0
		rgbData[rgbIndex++] = rgba[i + 2] ?? 0
	}

	return rgbData
}

export function rgbToRgbaData(rgb: Uint8Array): Uint8Array {
	const totalPixels = rgb.length / 3
	const rgbaData = new Uint8Array(totalPixels * 4)

	let rgbaIndex = 0
	for (let i = 0; i < rgb.length; i += 3) {
		rgbaData[rgbaIndex++] = rgb[i] ?? 0 // R
		rgbaData[rgbaIndex++] = rgb[i + 1] ?? 0 // G
		rgbaData[rgbaIndex++] = rgb[i + 2] ?? 0 // B
		rgbaData[rgbaIndex++] = 255 // A
	}
	return rgbaData
}

export function getColorAndWeight(options: {
	inputImageBitmap: ImageBitmap
	faceColors: Float32Array | null
	faceWeights: Float32Array | null
}) {
	const { inputImageBitmap, faceColors, faceWeights } = options

	const targetSize = fitToSize([inputImageBitmap.width, inputImageBitmap.height], [300, 300])

	const resizedInputImage = resizeImageBitmapToBitmap(
		inputImageBitmap,
		targetSize[0],
		targetSize[1],
	)
	const imageData = bitmapToImageData(resizedInputImage)
	resizedInputImage.close()

	const { colors, weights: pixelWeights } = extractColors({
		rgbData: getRgbData(imageData),
		faceColors,
		faceWeights,
	})

	return { colors, pixelWeights }
}

// helper functions to save luted images for debugging
export async function saveLutedImage(imageData: ImageData, lut3d: Float32Array, filename: string) {
	const imageFloat32 = normalizeToFloat32Array(getRgbData(imageData))
	const imageFloat32Luted = apply3DLUT(imageFloat32, lut3d, 25)
	await saveFloat32ArrayAsImage(imageFloat32Luted, imageData.width, imageData.height, filename)
}

export async function saveFloat32ArrayAsImage(
	image: Float32Array,
	width: number,
	height: number,
	filename: string,
) {
	const data = new Uint8ClampedArray(width * height * 4)
	for (let i = 0; i < image.length / 3; i++) {
		data[i * 4] = clamp((image[i * 3] as number) * 255, 0, 255)
		data[i * 4 + 1] = clamp((image[i * 3 + 1] as number) * 255, 0, 255)
		data[i * 4 + 2] = clamp((image[i * 3 + 2] as number) * 255, 0, 255)
		data[i * 4 + 3] = 255
	}
	const imageDataResult = new ImageData(data, width, height)
	const bitmap = await imageDataToBitmap(imageDataResult)
	await debugSaveBitmap(bitmap, filename)
}

// For debugging, put in color-match-processing.ts:
// if (!useParameters) {
// 	const inputImage = await AI.renderSrgbInputWithSize(1600, {
// 		colorProfile: ColorProfileName.Standard,
// 	}, "fit", true)
// 	const inputImageBitmap = await imageDataToBitmap(inputImage)
// 	const inputImagePreset = await AI.renderSrgbInputWithSize(1600, {})
// 	const inputImagePresetBitmap = await imageDataToBitmap(inputImagePreset)
// 	const asShotWhiteBalance = await AI.getWhiteBalance()
// 	const profileCurve = await AI.getProfileCurve()
// 	const baseRenderOptions = {
// 		asShotTemp: asShotWhiteBalance.asShotTemperature,
// 		asShotTint: asShotWhiteBalance.asShotTint,
// 		profileCurve,
// 		colorLookTable: await AI.getColorLookTable(),
// 		profileLookTable: await AI.getProfileLookTable(),
// 	}
// 	await renderPreset({
// 		inputImageBitmap,
// 		inputPresetBitmap: inputImagePresetBitmap,
// 		adjustments: adjustments ?? {},
// 		baseRenderOptions,
// 		lut3d: mergedLut3d,
// 		filename: file.name.split(".")[0] ?? "input",
// 	})
// }
export async function renderPreset(options: {
	inputImageBitmap: ImageBitmap
	inputPresetBitmap: ImageBitmap
	adjustments: Partial<Adjustments>
	baseRenderOptions: RenderOptions
	lut3d: Float32Array
	filename: string
}) {
	const { inputImageBitmap, inputPresetBitmap, adjustments, baseRenderOptions, lut3d, filename } =
		options
	const targetSize = fitToSize([inputImageBitmap.width, inputImageBitmap.height], [600, 600])

	const resizedInputImage = resizeImageBitmapToBitmap(
		inputImageBitmap,
		targetSize[0],
		targetSize[1],
	)
	const imageData = bitmapToImageData(resizedInputImage)
	await debugSaveBitmap(resizedInputImage, filename + "+inputResized.png")

	await debugSaveBitmap(inputPresetBitmap, filename + "+inputPreset.png")

	const inputBefore = normalizeToFloat32Array(getRgbData(imageData))

	const inputPreset = renderCpu(
		inputBefore,
		{},
		{
			inputColorSpace: ColorSpaceCpu.sRGB,
			outColorSpace: ColorSpaceCpu.sRGB,
			...baseRenderOptions,
		},
	)

	await saveFloat32ArrayAsImage(
		inputPreset,
		imageData.width,
		imageData.height,
		filename + "+InputPresetCpu.png",
	)

	const inputAdjustments = renderCpu(inputBefore, adjustments, {
		inputColorSpace: ColorSpaceCpu.sRGB,
		outColorSpace: ColorSpaceCpu.sRGB,
		...baseRenderOptions,
	})

	await saveFloat32ArrayAsImage(
		inputAdjustments,
		imageData.width,
		imageData.height,
		filename + "+AdjustmentsCpu.png",
	)

	const imageFloat32Luted = transformImageWithMergedLut({
		image: inputPreset,
		mergedLut3d: lut3d,
	})
	await saveFloat32ArrayAsImage(
		imageFloat32Luted,
		imageData.width,
		imageData.height,
		filename + "+CpuLuted.png",
	)

	resizedInputImage.close()
}
